// IP access check (canvas-style soft gate / Layer 1).
//
// This is a UX nudge, NOT a security boundary — the real enforcement is the
// BFF IP middleware (Layer 2). Like canvas, it FAILS OPEN on any error so a
// transient hiccup never blocks a legitimate, whitelisted user. A blocked IP
// is only ever reported when the BFF explicitly says the IP is not whitelisted.

import config from "./config.js";

/**
 * Ask the BFF whether the caller's IP is whitelisted. The BFF derives the IP
 * from the request itself (x-forwarded-for), so the browser does not need to
 * detect its own IP.
 *
 * @returns {Promise<{allowed: boolean, ip: string|null}>}
 */
export const checkIpAccess = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(`${config.apiBaseUrl}/ip-check`, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });

    // Any non-OK response: fail open to avoid breaking a legitimate user.
    if (!response.ok) {
      return { allowed: true, ip: null };
    }

    const data = await response.json();
    if (data && data.isWhitelisted === false) {
      return { allowed: false, ip: data.ip || null };
    }

    return { allowed: true, ip: (data && data.ip) || null };
  } catch (error) {
    // Network/parse/timeout error → fail open (same posture as canvas).
    console.warn("[IP gate] ip-check failed, allowing access:", error);
    return { allowed: true, ip: null };
  } finally {
    clearTimeout(timeoutId);
  }
};
